import { useApiQuery } from '../useApiQuery';
import { dashboardKeys } from '@/lib/queryKeys';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import { dayBoundStart } from '@/hooks/bookings/useBookings';
import { dateKeyInTimeZone } from '@/lib/date-values';

export interface UpcomingJob {
  id: string;
  jobId?: string | null;
  appointmentId?: string | null;
  customerName: string;
  service: string;
  scheduledAt: string;
  /** §15 — day-bound visit (scheduledDate, no startAt yet): `scheduledAt` is the
   *  day anchored at tenant-local midnight, so render the DAY only, no clock time. */
  dayBound: boolean;
  status: string;
  /** Assigned tech name (direct assignment or first crew member), for the board. */
  technicianName: string | null;
  /** Appointment confirmation state — drives the Confirmed / To confirm badge. */
  confirmationStatus: string | null;
  /** Scheduled end instant, when known — lets the board size a Gantt block. */
  endsAt: string | null;
}

function pickItems(raw: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  const items = obj.items ?? obj.data ?? obj.bookings ?? obj.detailData;
  return Array.isArray(items) ? (items as Array<Record<string, unknown>>) : [];
}

/**
 * Assigned tech name off a `GET /bookings` row. The list response carries the
 * tech at the TOP LEVEL (`technicianName`, or `technicianNames[0]` /
 * `technicians[0].name`), not under a nested `technician`/`crewAssignments`
 * object — reading only the nested shape left every board item "Unassigned" and
 * collapsed the per-tech Gantt into one lane. Nested shapes are kept as a
 * fallback for any other caller that still returns them.
 */
function pickTechName(b: Record<string, unknown>): string | null {
  if (typeof b.technicianName === 'string' && b.technicianName) {
    return b.technicianName;
  }
  const names = b.technicianNames;
  if (Array.isArray(names) && typeof names[0] === 'string' && names[0]) {
    return names[0];
  }
  const techs = b.technicians;
  if (Array.isArray(techs)) {
    const n = (techs[0] as { name?: unknown } | undefined)?.name;
    if (typeof n === 'string' && n) return n;
  }
  const tech = b.technician as { name?: unknown } | undefined;
  if (typeof tech?.name === 'string' && tech.name) return tech.name;
  const crew = b.crewAssignments as Array<{ technician?: { name?: unknown } }> | undefined;
  const cn = crew?.[0]?.technician?.name;
  return typeof cn === 'string' && cn ? cn : null;
}

function toUpcomingJob(b: Record<string, unknown>): UpcomingJob {
  const contact = b.contact as Record<string, unknown> | undefined;
  const serviceType = b.serviceType as Record<string, unknown> | undefined;
  const id = String(b.bookingId ?? b.id ?? '');
  // §15/§7B — the `scheduled` statusGroup includes day-bound visits (scheduledDate
  // set, startAt still null: awaiting the route optimizer). They ARE upcoming
  // work — anchor their day like useBookings does, never render an empty date.
  const timedStart = b.startAt ?? b.scheduledStart;
  const startInstant = timedStart ?? dayBoundStart(b);
  return {
    id,
    jobId: typeof b.jobId === 'string' ? b.jobId : null,
    appointmentId: id || null,
    customerName: String(
      b.customerName ?? [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') ?? '',
    ),
    service: String(serviceType?.name ?? b.serviceName ?? b.serviceType ?? ''),
    scheduledAt: String(startInstant ?? ''),
    dayBound: !timedStart && Boolean(startInstant),
    status: String(b.status ?? ''),
    technicianName: pickTechName(b),
    // `GET /bookings` returns the customer confirmation on `customerConfirmationStatus`;
    // fall back to `confirmationStatus` for any other caller.
    confirmationStatus:
      typeof b.customerConfirmationStatus === 'string'
        ? b.customerConfirmationStatus
        : typeof b.confirmationStatus === 'string'
          ? b.confirmationStatus
          : null,
    endsAt:
      typeof (b.endAt ?? b.scheduledEnd) === 'string' ? String(b.endAt ?? b.scheduledEnd) : null,
  };
}

/**
 * Next scheduled appointments for the Home widget — from the real bookings
 * endpoint on operations (`GET /bookings`), upcoming-first. Replaces the dead
 * legacy `/api/dashboard/fetchJobData`.
 */
export function useUpcomingJobs(timeZone?: string) {
  // The bookings endpoint validates dateFrom/dateTo as YYYY-MM-DD and
  // interprets them in the tenant timezone, so derive the window bounds as
  // tenant-local dates. A UTC-sliced `new Date().toISOString()` would shift a
  // day in non-UTC zones near midnight and drop tonight's jobs / pull tomorrow's.
  const fromKey = dateKeyInTimeZone(new Date().toISOString(), timeZone);
  const toKey = dateKeyInTimeZone(new Date(Date.now() + 14 * 86_400_000).toISOString(), timeZone);
  return useApiQuery<UpcomingJob[]>({
    queryKey: [...dashboardKeys.upcomingJobs(), fromKey],
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        schedulerEndpoints.bookings(),
        {
          params: {
            statusGroup: 'scheduled',
            // A bounded WINDOW instead of an open-ended page: the server
            // sorts by NULLABLE startAt, so with any page size a day-bound
            // visit (startAt null) can be ordered behind every timed visit
            // and never reach the client. Two weeks × 100 rows is a complete
            // set for a 5-item widget; the effective start
            // (startAt ?? scheduledDate) only exists client-side — sort +
            // slice here.
            dateFrom: fromKey,
            dateTo: toKey,
            sortBy: 'startAt',
            sortOrder: 'asc',
            excludeUnscheduled: 'true',
            limit: 100,
            offset: 0,
          },
        },
      );
      return pickItems(raw)
        .filter((b) => b.bookingId ?? b.id)
        .map(toUpcomingJob)
        .filter((j) => j.scheduledAt !== '')
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
        .slice(0, 12);
    },
    showErrorToast: false,
  });
}

/**
 * Today's scheduled jobs for the Home "Today on the board" — scoped to the
 * FULL tenant-local day (00:00–23:59), not "next N from now". This shows the
 * whole day per tech (including this morning) and never truncates a busy day to
 * a handful of upcoming items.
 */
export function useTodayBoardJobs(timeZone?: string) {
  const todayKey = dateKeyInTimeZone(new Date().toISOString(), timeZone);
  return useApiQuery<UpcomingJob[]>({
    queryKey: dashboardKeys.todayBoard(todayKey),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        schedulerEndpoints.bookings(),
        {
          params: {
            statusGroup: 'scheduled',
            // Single tenant-local day. The endpoint takes YYYY-MM-DD in
            // tenant-local terms, so pass `todayKey` directly — slicing the UTC
            // instant of local 23:59 rolls to the next date in negative-offset
            // zones (e.g. LA), which would pull tomorrow's bookings into today.
            dateFrom: todayKey,
            dateTo: todayKey,
            sortBy: 'startAt',
            sortOrder: 'asc',
            excludeUnscheduled: 'true',
            limit: 100,
            offset: 0,
          },
        },
      );
      return pickItems(raw)
        .filter((b) => b.bookingId ?? b.id)
        .map(toUpcomingJob)
        .filter((j) => j.scheduledAt !== '')
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    },
    showErrorToast: false,
  });
}
